import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Booking.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function Booking() {
  const loc = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    boatMake: '',
    boatModel: '',
    boatYear: '',
    engineType: '',
    engineBrand: '',
    serviceType: '',
    location: '',
    description: '',
    photos: [],
    accessible: false,
    urgent: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const service = params.get('service');
    if (service) {
      setFormData(prev => ({ ...prev, serviceType: service }));
    }
  }, [loc.search]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: Array.from(files)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use FormData to handle file uploads
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('boatMake', formData.boatMake);
      submitData.append('boatModel', formData.boatModel);
      submitData.append('boatYear', formData.boatYear);
      submitData.append('engineType', formData.engineType);
      submitData.append('engineBrand', formData.engineBrand);
      submitData.append('serviceType', formData.serviceType);
      submitData.append('location', formData.location);
      submitData.append('description', formData.description);
      submitData.append('accessible', formData.accessible);
      submitData.append('urgent', formData.urgent);

      // Add photos
      formData.photos.forEach(photo => {
        submitData.append('photos', photo);
      });

      const response = await fetch(`${API_URL}/api/booking`, {
        method: 'POST',
        body: submitData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit booking');
      }

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          phone: '',
          email: '',
          boatMake: '',
          boatModel: '',
          boatYear: '',
          engineType: '',
          engineBrand: '',
          serviceType: '',
          location: '',
          description: '',
          photos: [],
          accessible: false,
          urgent: false
        });
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-page">
      <section className="booking-hero">
        <div className="container">
          <h1>Book Your Service</h1>
          <p>Fill out the form below to schedule your marine service. We'll confirm your appointment within 24 hours.</p>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="booking-layout">
            <div className="booking-form-container">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h2>Booking Submitted!</h2>
                  <p>Thank you for your booking request. We'll contact you within 24 hours to confirm your appointment.</p>
                  <p className="success-contact">For urgent matters, call us at <a href="tel:8503230769">850-323-0769</a></p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="booking-form">
                  <h2>Your Information</h2>
                  
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        pattern="[\d\s\-\(\)\+]{10,}"
                        title="Please enter a valid phone number (at least 10 digits)"
                        placeholder="850-323-0769"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email address"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <h2>Boat Information</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="boatMake">Boat Make</label>
                      <input
                        type="text"
                        id="boatMake"
                        name="boatMake"
                        value={formData.boatMake}
                        onChange={handleChange}
                        placeholder="e.g., Boston Whaler"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="boatModel">Boat Model</label>
                      <input
                        type="text"
                        id="boatModel"
                        name="boatModel"
                        value={formData.boatModel}
                        onChange={handleChange}
                        placeholder="e.g., Montauk"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="boatYear">Year</label>
                      <input
                        type="text"
                        id="boatYear"
                        name="boatYear"
                        value={formData.boatYear}
                        onChange={handleChange}
                        placeholder="e.g., 2020"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="engineType">Engine Type *</label>
                      <select
                        id="engineType"
                        name="engineType"
                        value={formData.engineType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select engine type</option>
                        <option value="outboard">Outboard</option>
                        <option value="inboard">Inboard</option>
                        <option value="sterndrive">Sterndrive</option>
                        <option value="jet">Jet Drive</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="engineBrand">Engine Brand</label>
                      <input
                        type="text"
                        id="engineBrand"
                        name="engineBrand"
                        value={formData.engineBrand}
                        onChange={handleChange}
                        placeholder="e.g., Yamaha, Mercury"
                      />
                    </div>
                  </div>

                  <h2>Service Details</h2>

                  <div className="form-group">
                    <label htmlFor="serviceType">Service Type *</label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select service type</option>
                      <option value="engine-diagnostics">Engine Diagnostics & Repair</option>
                      <option value="routine-maintenance">Routine Maintenance</option>
                      <option value="electrical">Electrical & Battery Systems</option>
                      <option value="bilge-pumps">Bilge / Pumps / Plumbing</option>
                      <option value="steering">Steering & Controls</option>
                      <option value="safety-check">Pre-Trip Safety Check</option>
                      <option value="installation">Installation</option>
                      <option value="other">Other / Not Sure</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="location">Boat Location *</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Marina name or address"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">What's happening? *</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe the issue or service you need..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="photos">Photos (optional)</label>
                    <input
                      type="file"
                      id="photos"
                      name="photos"
                      onChange={handleChange}
                      accept="image/*"
                      multiple
                      className="file-input"
                    />
                    <p className="form-help">Upload photos of the issue, console, or engine label plate if possible</p>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="accessible"
                        checked={formData.accessible}
                        onChange={handleChange}
                        required
                      />
                      <span>I confirm the boat will be accessible at scheduled time *</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="urgent"
                        checked={formData.urgent}
                        onChange={handleChange}
                      />
                      <span>This is an emergency / urgent request</span>
                    </label>
                  </div>

                  {formData.urgent && (
                    <div className="urgent-message">
                      <p>⚠️ For urgent requests, please call us immediately at <a href="tel:8503230769">850-323-0769</a></p>
                    </div>
                  )}

                  {error && <div className="error-message">{error}</div>}
                  <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit Booking Request'}
                  </button>
                </form>
              )}
            </div>

            <div className="booking-info">
              <div className="info-card">
                <h3>What to Expect</h3>
                <ul>
                  <li>✓ Confirmation within 24 hours</li>
                  <li>✓ Arrival window provided</li>
                  <li>✓ Professional on-site service</li>
                  <li>✓ Clear communication throughout</li>
                  <li>✓ Payment after work is complete</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Have Questions?</h3>
                <p>Need help or have questions about your booking?</p>
                <a href="tel:8503230769" className="btn btn-secondary">📞 Call Now</a>
                <a href="mailto:Jeremy@islandmobilemarine.com" className="btn btn-secondary">✉️ Email Us</a>
              </div>

              <div className="info-card">
                <h3>Preparation Tips</h3>
                <ul>
                  <li>Ensure boat is accessible</li>
                  <li>Clear work area if possible</li>
                  <li>Have keys/access ready</li>
                  <li>Note any recent issues</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Booking;
